import { useEffect, useMemo, useRef, useState } from 'react';
import { AlphaJumpBar } from './AlphaJumpBar';
import {
  archiveClient,
  hardDeleteClient,
  restoreClient,
  useArchivedClients,
  useClients,
  useClientStats,
  useClientTiers,
  useLatestClientPhotoUrls,
  type ClientRow,
  type Tier,
} from '@busy/api';
import { useToast } from '@busy/ui';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';
import {
  clientSortKey,
  groupClientsByLetter,
} from '../../lib/clientGrouping';
import { CompactClientRow } from './CompactClientRow';

// Apple-Contacts-style client manager. Shared by the Clients route and the
// Settings → Clients panel so both surfaces have one source of truth for
// browsing / multi-selecting / bulk-archiving / restoring / deleting.
//
// Layout:
//   - Filter chip row at the top (All / VIP / Regular / Active / New /
//     At-risk / Archived) — replaces the legacy bottom-of-card
//     Active/Archived tabs. Chip selection drives the data source
//     (active vs archived) AND the tier filter on top of that.
//   - A-Z grouped scrollable list with sticky letter headers
//   - Vertical A-Z jump sidebar pinned to the right edge (mobile-friendly
//     tap targets; only shows letters that have at least one client)
//   - Search row at the bottom (Select chip + search input + Add new)
//   - "Select" button toggles multi-select mode → checkbox column appears,
//     "Select all" button shows up, sticky bulk-action bar at the bottom
//     (Archive/Delete when an active chip is selected, Restore/Delete on
//     the Archived chip)

/** Filter category for the client roster. The Clients route lifts this
 *  state up so the tier-breakdown tile above the list can act as the
 *  filter control (tap a tier row → list narrows to that tier). When
 *  callers don't pass `chip`, the list defaults to 'all' active. */
export type ChipKey = 'all' | 'vip' | 'regular' | 'active' | 'new' | 'at-risk' | 'archived';

const TIER_CHIPS: ChipKey[] = ['vip', 'regular', 'active', 'new', 'at-risk'];

export function AlphaClientList({
  onOpenClient,
  onAddNewClient,
  maxHeight,
  chip = 'all',
}: {
  /** Called when a row is tapped (out of select mode). The host page
   *  controls the detail modal so deep-linking keeps working. */
  onOpenClient: (id: string) => void;
  /** When provided, renders a sticky "+ Add new client" tile at the top
   *  of the list. Tapping it calls this callback — the host wires it to
   *  whatever new-client flow makes sense for that surface (modal,
   *  inline form, etc.). */
  onAddNewClient?: () => void;
  /** Optional CSS max-height (e.g. "60vh") for the list card. When unset,
   *  the card uses h-full and assumes the parent is viewport-bounded
   *  (the /clients route + cut-timer modal). When set, the card caps
   *  itself for use inside non-viewport-fit hosts (Settings panel). */
  maxHeight?: string;
  /** Controlled filter category. The Clients route drives this from the
   *  ClientTiersTile so the tile doubles as filter UI. Hosts that don't
   *  filter (Settings → Clients CSV panel) just leave this as 'all'. */
  chip?: ChipKey;
}) {
  const t = useT();
  const toast = useToast();
  const language = useUI((s) => s.language);
  const clients = useClients();
  const archived = useArchivedClients();
  const tiers = useClientTiers();
  // Stats fuels the Big Tipper flag on each row. Already a cached hook
  // mounted by the Clients route at the page level, so this is a cache
  // hit; the Settings panel host gets its own fetch on first mount.
  const stats = useClientStats();
  const photoUrls = useLatestClientPhotoUrls();

  const [search, setSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const scrollRootRef = useRef<HTMLDivElement>(null);

  // Drop selection when the chip flips so an Active selection doesn't
  // accidentally bulk-restore when switching to the Archived chip (or
  // vice versa). Same precaution that previously fired on tab change.
  useEffect(() => {
    setSelectedIds(new Set());
    setSelectMode(false);
  }, [chip]);

  const tierByClient = useMemo(() => {
    const m = new Map<string, Tier>();
    (tiers.data ?? []).forEach((tt) => m.set(tt.client_id, tt.tier));
    return m;
  }, [tiers.data]);

  const bigTipperByClient = useMemo(() => {
    const m = new Map<string, boolean>();
    (stats.data ?? []).forEach((s) => {
      if (s.is_big_tipper) m.set(s.client_id, true);
    });
    return m;
  }, [stats.data]);

  // Source list flips between archived and active based on the chip. The
  // tier breakdown only applies to active rows (the client_tier view
  // excludes archived clients), so any tier-specific chip implicitly
  // pulls from the active list.
  const sourceList = (chip === 'archived' ? archived.data : clients.data) ?? [];

  // Per-tier counts for chip badges. Computed once per tiers update so
  // each chip can show its own bucket count without re-iterating the
  // active list per chip render.
  const tierCounts = useMemo(() => {
    const m = { vip: 0, regular: 0, active: 0, new: 0, 'at-risk': 0 } as Record<
      Exclude<ChipKey, 'all' | 'archived'>,
      number
    >;
    for (const row of tiers.data ?? []) {
      m[row.tier] += 1;
    }
    return m;
  }, [tiers.data]);

  // Apply chip filter (when tier-specific) + search filter, then
  // alphabetize by last_name → first_name. The active/archived split is
  // handled by the chip selection above; tier-specific chips also narrow
  // the active list to one bucket.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const digits = q.replace(/\D/g, '');
    let list = sourceList;
    if (TIER_CHIPS.includes(chip)) {
      list = list.filter((c) => tierByClient.get(c.id) === chip);
    }
    if (q) {
      list = list.filter((c) => {
        const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
        const phone = c.phone?.replace(/\D/g, '') ?? '';
        return name.includes(q) || (digits.length > 0 && phone.includes(digits));
      });
    }
    return [...list].sort((a, b) => clientSortKey(a).localeCompare(clientSortKey(b)));
  }, [sourceList, chip, tierByClient, search]);

  const sections = useMemo(() => groupClientsByLetter(filtered), [filtered]);

  function toggleId(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedIds(new Set(filtered.map((c) => c.id)));
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function exitSelectMode() {
    setSelectMode(false);
    clearSelection();
  }

  async function bulkApply(action: 'archive' | 'restore' | 'delete') {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (action === 'delete') {
      const ok = confirm(t('clients.bulk.deleteConfirm', { count: ids.length }));
      if (!ok) return;
    }
    setBusy(true);
    const fn = action === 'archive' ? archiveClient : action === 'restore' ? restoreClient : hardDeleteClient;
    const results = await Promise.allSettled(ids.map((id) => fn(id)));
    setBusy(false);
    const failures = results.filter((r) => r.status === 'rejected').length;
    if (failures > 0) {
      toast(t('clients.bulk.partialError', { ok: ids.length - failures, fail: failures }), 'error');
    } else {
      const key =
        action === 'archive'
          ? 'clients.bulk.archived'
          : action === 'restore'
          ? 'clients.bulk.restored'
          : 'clients.bulk.deleted';
      toast(t(key, { count: ids.length }), 'success');
    }
    exitSelectMode();
  }

  const presentLetters = new Set(sections.map(([L]) => L));
  const totalCount = sourceList.length;
  const archivedCount = (archived.data ?? []).length;
  const activeCount = (clients.data ?? []).length;
  const selectedCount = selectedIds.size;
  const allFilteredSelected =
    filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));

  // Count for the currently-selected chip (drives the chip badge AND the
  // total-empty vs no-matches branching below).
  const chipCount =
    chip === 'all'
      ? activeCount
      : chip === 'archived'
        ? archivedCount
        : tierCounts[chip];

  return (
    // Fills its parent and arranges children vertically. Controls (tabs,
    // search, summary, add-new) are shrink-0; the list region is flex-1
    // so it absorbs all remaining vertical space. Combined with the body
    // scroll lock applied by the host route (Clients tab + Settings →
    // Clients section), the only scrollable element on the page is the
    // list inside this card. No page scroll, no overflow spillage.
    <div className="relative h-full flex flex-col min-h-0">
      {/* Selection summary bar — only renders when in select mode.
          The Select / Cancel chip itself lives inside the bottom search
          row (left of the search input) so the top of the card is
          flush — frees vertical space and matches iOS Contacts. */}
      {selectMode && (
        <div className="shrink-0 flex items-center gap-3 mb-2 text-xs">
          <button
            type="button"
            onClick={allFilteredSelected ? clearSelection : selectAll}
            className="font-bold text-navy-900 dark:text-gold-400 underline-offset-4 hover:underline"
          >
            {allFilteredSelected ? t('clients.select.deselectAll') : t('clients.select.selectAll')}
          </button>
          <span className="text-ink-500">
            {t('clients.select.count', { n: selectedCount })}
          </span>
        </div>
      )}

      {/* Card frame: rounded outer with border, fills remaining vertical
          space. Children are the list area (flex-1 — list, empty, or
          loading state lives here) and the search row (shrink-0, pinned
          at the bottom iOS-style).

          Sizing: when `maxHeight` is provided the card uses an explicit
          `height` (NOT just max-height) so its flex children get a
          definite size to distribute. With only max-height, the relative
          wrapper inside has 0 intrinsic height (its scroll content is
          absolutely positioned), so flex-1 has nothing to grow into and
          the whole list collapses to just the bottom search bar. Settings
          panel hit this; the Clients route doesn't because its parent
          already provides h-screen → definite height through flex-1. */}
      <div
        className={`rounded-xl border border-line bg-surface overflow-hidden flex flex-col ${
          maxHeight ? '' : 'flex-1 min-h-0'
        }`}
        style={maxHeight ? { height: maxHeight } : undefined}
      >
        {/* List area — the only scrollable region. Alpha bar absolute-
            positions inside this wrapper so it spans the LIST height
            only, not the search bar below. */}
        <div className="relative flex-1 min-h-0">
          {((chip === 'archived' && archived.loading) ||
            (chip !== 'archived' && (clients.loading || tiers.loading))) ? (
            <div className="absolute inset-0 overflow-y-auto p-2">
              <SkeletonRows count={6} />
            </div>
          ) : totalCount === 0 ? (
            <div className="absolute inset-0 overflow-y-auto p-3">
              <EmptyState
                title={
                  chip === 'archived'
                    ? t('clients.empty.archivedTitle')
                    : t('clients.empty.title')
                }
                sub={
                  chip === 'archived'
                    ? t('clients.empty.archivedSub')
                    : t('clients.empty.sub')
                }
              />
            </div>
          ) : chipCount === 0 && (chip === 'all' || chip === 'archived') === false ? (
            // Tier-specific chip with zero in-bucket clients — softer
            // empty state than "no matches" since the user just hasn't
            // reached anyone of that tier yet.
            <div className="absolute inset-0 overflow-y-auto p-3">
              <EmptyState
                title={t('clients.tierEmpty.title')}
                sub={t('clients.tierEmpty.sub')}
              />
            </div>
          ) : filtered.length === 0 ? (
            <div className="absolute inset-0 overflow-y-auto p-3">
              <EmptyState
                title={t('clients.noMatches.title')}
                sub={t('clients.noMatches.sub')}
              />
            </div>
          ) : (
            <>
              <div
                ref={scrollRootRef}
                className="absolute inset-0 overflow-y-auto overscroll-contain [&::-webkit-scrollbar]:hidden"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <ul className="divide-y divide-line">
                  {sections.map(([letter, rows]) => (
                    <li key={letter}>
                      <h3
                        data-letter={letter}
                        className="sticky top-0 z-[1] px-3 py-1 bg-bg-alt text-[10px] tracking-[0.16em] uppercase font-extrabold text-ink-500 border-b border-line"
                      >
                        {letter}
                      </h3>
                      <ul className="divide-y divide-line">
                        {rows.map((c) => (
                          <li key={c.id}>
                            <CompactClientRow
                              client={c}
                              photoUrl={photoUrls.data?.[c.id]}
                              tier={chip === 'archived' ? undefined : tierByClient.get(c.id)}
                              isBigTipper={chip === 'archived' ? false : bigTipperByClient.get(c.id) ?? false}
                              subtitle={subtitleFor(c)}
                              selectMode={selectMode}
                              selected={selectedIds.has(c.id)}
                              onToggleSelect={() => toggleId(c.id)}
                              onOpen={() => onOpenClient(c.id)}
                              language={language}
                            />
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
              <AlphaJumpBar
                presentLetters={presentLetters}
                ariaLabel={t('clients.jumpAria')}
                jumpToLabel={(L) => t('clients.jumpTo', { letter: L })}
                scrollContainerRef={scrollRootRef}
              />
            </>
          )}
        </div>

        {/* Bottom search row — iOS-style, always visible at the bottom of
            the card. Carries:
              [Select / Cancel chip] [Search input] [+ Add-new button]
            Putting the Select chip here (instead of a dedicated row at
            the top of the card) gives more vertical room to the list
            and keeps the page strictly within the viewport. Frees the
            "scroll-back-to-top" path on tall rosters. */}
        <div className="shrink-0 border-t border-line bg-surface p-2 flex items-center gap-2">
          <button
            type="button"
            onClick={selectMode ? exitSelectMode : () => setSelectMode(true)}
            disabled={!selectMode && filtered.length === 0}
            className="shrink-0 h-10 px-3 rounded-lg border border-line bg-bg-alt text-xs font-bold text-ink-700 disabled:opacity-50"
          >
            {selectMode ? t('clients.select.cancel') : t('clients.select.start')}
          </button>
          <input
            type="search"
            placeholder={t('clients.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-0 h-10 px-4 rounded-lg border border-line bg-bg-alt text-ink-900 placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-navy-900 dark:focus:ring-gold-500"
          />
          {onAddNewClient && (
            <button
              type="button"
              onClick={onAddNewClient}
              aria-label={t('clients.addNew.title')}
              className="shrink-0 h-10 w-10 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 grid place-items-center font-bold shadow-sm active:scale-95 transition-transform"
            >
              <UserPlusIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Sticky bulk-action bar at the bottom of the viewport when in
          select mode and at least one row is checked. pb expands with the
          home-indicator safe area so the buttons aren't covered by the
          bezel on iPhone X+ or by Safari's bottom toolbar. The primary
          action flips Archive ↔ Restore based on whether the Archived
          chip is the active filter. */}
      {selectMode && selectedCount > 0 && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 px-3 pt-2 border-t border-line bg-surface/95 backdrop-blur-sm pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          role="region"
          aria-label={t('clients.bulk.aria')}
        >
          <div className="max-w-page mx-auto flex items-center gap-2">
            {chip === 'archived' ? (
              <button
                type="button"
                onClick={() => bulkApply('restore')}
                disabled={busy}
                className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold disabled:opacity-50"
              >
                {t('clients.bulk.restore', { n: selectedCount })}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => bulkApply('archive')}
                disabled={busy}
                className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold disabled:opacity-50"
              >
                {t('clients.bulk.archive', { n: selectedCount })}
              </button>
            )}
            <button
              type="button"
              onClick={() => bulkApply('delete')}
              disabled={busy}
              className="flex-1 h-11 rounded-lg border-2 border-red-500/70 bg-surface text-red-600 dark:text-red-400 font-bold disabled:opacity-50"
            >
              {t('clients.bulk.delete', { n: selectedCount })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// (ClientFilterChip removed — the ClientTiersTile on the Clients route
// is now the canonical filter UI. AlphaClientList just consumes the
// `chip` prop and filters accordingly.)

// --- helpers ---

function subtitleFor(c: ClientRow): string | undefined {
  // Phone is the most useful at-a-glance subtitle. If empty, we leave the
  // line blank — keeps the row tight.
  return c.phone || undefined;
}

function SkeletonRows({ count }: { count: number }) {
  return (
    <ul className="rounded-2xl border border-line bg-surface overflow-hidden divide-y divide-line">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="flex items-center gap-3 px-4 py-3">
          <span className="w-10 h-10 rounded-full bg-bg-alt animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <span className="block h-3 w-32 rounded bg-bg-alt animate-pulse" />
            <span className="block h-2.5 w-24 rounded bg-bg-alt animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-bg-alt p-8 text-center">
      <h3 className="font-display text-lg mb-1">{title}</h3>
      <p className="text-sm text-ink-500 leading-relaxed max-w-sm mx-auto">{sub}</p>
    </div>
  );
}

function UserPlusIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="8.5" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
}
